import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { Page } from '@/shared/ui/Page';
import { PageHeader } from '@/shared/ui/PageHeader';
import { GreenLiquidGlassButton } from '@/shared/ui/GreenLiquidGlassButton';
import { subscriptionApi } from '@/entities/subscription/api';
import { cn } from '@/shared/lib/cn';
import { useT } from '@/shared/lib/i18n';

type Scenario = 'prorate' | 'settle';

export function ApproveExit() {
  const { id, exitRequestId } = useParams<{ id: string; exitRequestId: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const t = useT();
  const [scenario, setScenario] = useState<Scenario>('prorate');
  const [exitDate, setExitDate] = useState(() => new Date().toISOString().slice(0, 10));

  const mutation = useMutation({
    mutationFn: () =>
      subscriptionApi.approveExit(exitRequestId!, { exit_date: exitDate, scenario }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['subscription-members', id] });
      await qc.invalidateQueries({ queryKey: ['subscription-exit-requests', id] });
      navigate(`/subscriptions/${id}`);
    },
  });

  const scenarios: { value: Scenario; label: string }[] = [
    { value: 'prorate', label: t('management.approveExit.scenarioProrate') },
    { value: 'settle', label: t('management.approveExit.scenarioSettle') },
  ];

  return (
    <Page>
      <PageHeader title={<span className="font-bold text-[19px]">{t('management.approveExit.title')}</span>} />
      <div className="px-[18px] pb-5 flex flex-col gap-4 min-h-0">
        <label className="flex flex-col gap-2">
          <span className="text-[14px] font-semibold text-text-secondary">{t('management.approveExit.exitDate')}</span>
          <input
            type="date"
            value={exitDate}
            onChange={(event) => setExitDate(event.target.value)}
            className="rounded-[12px] px-4 py-3 text-[15px] bg-white/55 border border-white/60"
          />
        </label>

        <div className="flex flex-col gap-2">
          <p className="text-[14px] font-semibold text-text-secondary">{t('management.approveExit.scenario')}</p>
          {scenarios.map((item) => (
            <button
              key={item.value}
              onClick={() => setScenario(item.value)}
              className={cn(
                'flex items-center gap-3 rounded-[12px] px-4 py-3 text-[14px] bg-white/50 border',
                scenario === item.value ? 'border-2 border-brand-green bg-brand-green/10' : 'border-white/60',
              )}
            >
              <span className={cn('w-[18px] h-[18px] rounded-full border-2', scenario === item.value ? 'border-brand-green bg-brand-green' : 'border-[#b9c4bb]')} />
              {item.label}
            </button>
          ))}
        </div>

        <div className="mt-auto">
          {mutation.isError && (
            <p className="text-[13px] text-status-debt text-center mb-2">{t('management.approveExit.error')}</p>
          )}
          <GreenLiquidGlassButton loading={mutation.isPending} onClick={() => mutation.mutate()}>
            {t('management.approveExit.submit')}
          </GreenLiquidGlassButton>
        </div>
      </div>
    </Page>
  );
}
